import { cn } from '../lib/cn';

export type LogoVariant = 'full' | 'mark' | 'wordmark';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';

export interface LogoProps {
  /**
   * `full` = glifo + wordmark (default).
   * `mark` = solo el glifo (icono favicon / avatar).
   * `wordmark` = solo el texto "ExamsReactor".
   */
  variant?: LogoVariant;
  size?: LogoSize;
  /** Force a theme ignoring the ambient CSS color scheme. */
  theme?: 'light' | 'dark';
  className?: string;
  /** `aria-label` for assistive tech. Falls back to "ExamsReactor". */
  label?: string;
}

const SIZE_MAP: Record<LogoSize, { mark: number; text: string; gap: string }> = {
  sm: { mark: 24, text: 'text-base', gap: 'gap-1.5' },
  md: { mark: 32, text: 'text-lg', gap: 'gap-2' },
  lg: { mark: 44, text: 'text-xl', gap: 'gap-2.5' },
  xl: { mark: 64, text: 'text-3xl', gap: 'gap-3' },
};

/**
 * Logo oficial de ExamsReactor renderizado 100% en codigo (SVG inline).
 *
 * Concepto: glifo tipo "reactor atomico" con 3 orbitas cruzadas y un nucleo
 * central. Wordmark geometrico sans. Colores:
 *   - Primary:  sky-500  (#0ea5e9) — reactor blue
 *   - Secondary: violet-500 (#8b5cf6)
 *   - Texto: heredado del tema (currentColor)
 *
 * Dark/light se maneja via `currentColor` del ancestro + el theme override
 * opcional. No hay PNG/JPG/ICO; esto es intencional (ADR-0001 D16).
 */
export function LogoExamsReactor({
  variant = 'full',
  size = 'md',
  theme,
  className,
  label = 'ExamsReactor',
}: LogoProps) {
  const { mark, text, gap } = SIZE_MAP[size];
  const themeClass = theme === 'light' ? 'text-slate-900' : theme === 'dark' ? 'text-slate-100' : '';

  return (
    <span
      className={cn('inline-flex items-center', gap, themeClass, className)}
      role="img"
      aria-label={label}
    >
      {(variant === 'full' || variant === 'mark') && <LogoMark size={mark} />}
      {(variant === 'full' || variant === 'wordmark') && (
        <span className={cn('font-bold tracking-tight select-none', text)}>
          <span className="text-[currentColor]">Exams</span>
          <span className="text-sky-500">Reactor</span>
        </span>
      )}
    </span>
  );
}

function LogoMark({ size }: { size: number }) {
  const id = 'logoER';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <title>ExamsReactor mark</title>
      <defs>
        <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="36" y2="36" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0ea5e9" />
          <stop offset="1" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <ellipse cx="18" cy="18" rx="14" ry="5" stroke={`url(#${id}-grad)`} strokeWidth="1.6" />
      <ellipse
        cx="18"
        cy="18"
        rx="14"
        ry="5"
        stroke={`url(#${id}-grad)`}
        strokeWidth="1.6"
        transform="rotate(60 18 18)"
      />
      <ellipse
        cx="18"
        cy="18"
        rx="14"
        ry="5"
        stroke={`url(#${id}-grad)`}
        strokeWidth="1.6"
        transform="rotate(120 18 18)"
      />
      <circle cx="18" cy="18" r="3.2" fill="#0ea5e9" />
      <circle cx="18" cy="18" r="1.3" fill="#fff" fillOpacity="0.9" />
    </svg>
  );
}
